import { useNotification } from "@context/NotificationContext";
import { completeTaskByVesselId } from "@services/tasksApi";
import { useMutation } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import type { AxiosError } from "axios";

type CompleteTaskParams = {
  status: string;
  internalNotes?: string;
  internalNotesDocument?: string;
  internalNotesDocumentName?: string;
  externalNotes?: string;
  externalNotesDocument?: string;
  externalNotesDocumentName?: string;
  delegatedToRole?: string;
  effectiveStartDate?: string;
  effectiveEndDate?: string;
  gracePeriod?: string;
  remarks?: string;
  version?: number;
};

type CompleteTaskVariables = {
  service: string;
  vesselId: string;
  params: CompleteTaskParams;
  successTitle?: string;
  successDescription?: string;
  errorTitle: string;
};

const getCompleteTaskAction = (params: CompleteTaskParams): string => {
  if (params.delegatedToRole) return "delegate_vessel_registration";
  if (params.effectiveStartDate || params.effectiveEndDate) return "approve_bunker_barge";
  if (params.status.includes("APPROVED")) return "approve_vessel_registration";
  if (params.status.includes("REJECTED")) return "reject_vessel_registration";
  return "complete_vessel_registration_task";
};

export const useCompleteTask = ({
  handleSuccess,
  handleError,
}: {
  handleSuccess?: () => void;
  handleError?: (error: AxiosError<{ detail?: string }>) => void;
}) => {
  const { notify } = useNotification();

  const mutation = useMutation({
    mutationFn: ({ service, vesselId, params }: CompleteTaskVariables) =>
      runTransaction(
        {
          module: "vessel_registration",
          screen: "VesselStatus",
          action: getCompleteTaskAction(params),
          entityType: "vessel",
        },
        (transactionId) =>
          completeTaskByVesselId({
            service,
            vesselId,
            params,
            ...(transactionId ? { transactionId } : {}),
          }),
      ),

    onSuccess: (_, variables) => {
      const { successTitle, successDescription } = variables;
      if (successTitle)
        notify({
          title: successTitle,
          description: successDescription,
          type: "success",
        });

      handleSuccess?.();
    },

    onError: (error: AxiosError<{ detail?: string }>, variables) => {
      const { errorTitle } = variables;

      notify({
        title: errorTitle,
        description: error.response?.data?.detail ?? error.message,
        type: "error",
      });

      handleError?.(error);
    },
  });

  return mutation;
};
